import React, { useState } from 'react';

import { uniqBy } from 'ramda';
import type { PartyEditorPopoverProps, PartySubset, TextColorType } from 'src/components/workflow';
import { labelPopoverOffsetPx, PartiesEditor, PartiesListBody } from 'src/components/workflow';
import type { Apollo } from 'src/gql/generated';
import { useGetIsAuthorizedToEditStorable, useSetDocumentParties } from 'src/hooks';
import type { UserWithOrg } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  listContainer: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
  },
  partyName: {
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
    cursor: 'pointer',
  },
}));

interface RepositoryPartiesListProps {
  document: DocumentWithParties;
  position: 'left' | 'right' | 'bottom';
  textColor?: TextColorType;
}

export function RepositoryPartiesList({
  document,
  position,
  textColor = 'textSecondary',
}: RepositoryPartiesListProps) {
  const { classes } = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const getOffsetPx = () => {
    switch (position) {
      case 'bottom':
        return 4;
      case 'left':
        return -labelPopoverOffsetPx;
      case 'right':
        return labelPopoverOffsetPx;
    }
  };

  return (
    <div ref={setAnchorEl} className={classes.listContainer}>
      <PartiesListBody parties={document.parties} setOpen={setOpen} textColor={textColor} />
      <RepositoryPartiesPopover
        document={document}
        popoverProps={{
          anchorEl,
          anchorOrigin:
            position === 'bottom'
              ? { horizontal: 'left', vertical: 'bottom' }
              : position === 'left'
                ? { horizontal: 'left', vertical: 'top' }
                : { horizontal: 'right', vertical: 'top' },

          onClose: () => setOpen(false),
          open,
          style: {
            transform: `translateX(${getOffsetPx()}px)`,
          },
          transformOrigin:
            position === 'bottom'
              ? { horizontal: 'left', vertical: 'top' }
              : position === 'left'
                ? { horizontal: 'right', vertical: 'top' }
                : { horizontal: 'left', vertical: 'top' },
        }}
      />
    </div>
  );
}

interface DocumentWithParties extends Pick<Apollo.Document, 'id' | 'requesterRole' | '__typename'> {
  owner: UserWithOrg;
  parties: PartySubset[];
}

export default function RepositoryPartiesPopover({
  document,
  popoverProps,
}: {
  document: DocumentWithParties;
  popoverProps: PartyEditorPopoverProps;
}) {
  const { setDocumentParties } = useSetDocumentParties();
  const { getIsAuthorizedToEditStorable } = useGetIsAuthorizedToEditStorable();
  const { canEdit } = getIsAuthorizedToEditStorable(document);

  async function handlePartyToggle(party: PartySubset) {
    const newPartyIds = new Set(document.parties.map(p => p.id));

    if (newPartyIds.has(party.id)) newPartyIds.delete(party.id);
    else newPartyIds.add(party.id);

    const newParties = uniqBy(
      p => p.id,
      [...document.parties, party].filter(p => newPartyIds.has(p.id))
    );

    await setDocumentParties({
      documentId: document.id,
      parties: newParties,
    });
  }

  return (
    <PartiesEditor
      disabledReason={!canEdit ? 'Only the document owner and editors can edit parties' : ''}
      popoverProps={popoverProps}
      selectedParties={document.parties}
      onPartyToggle={handlePartyToggle}
    />
  );
}
